import {useState} from 'react'
import useMessage from '@hooks/useMessage'
import useOptimisticCheckout from '@page-components/CheckoutV3/hooks/useOptimisticCheckout'
import {useClient, useMutate} from 'apollo-hooks'
import {UseMutateOptions} from 'apollo-hooks/dist/useMutate'
import {DocumentNode} from 'graphql'
import {useTranslation} from 'next-i18next'

export default function useCheckoutMutation(
  mutation: DocumentNode,
  options: {
    successMessage?: string
    onSuccess?: (data: any) => void
    refetchQueries?: string[]
    noSuccessMessage?: boolean
    onError?: (error: Error) => void
    clientName?: string
  } = {}
) {
  const {t} = useTranslation('generic', {keyPrefix: 'messages'})
  const {successMessage = t('changesSaved'), onSuccess = () => {}, refetchQueries} = options
  const [loading, setLoading] = useState(false)
  const client = useClient()
  const mutate = useMutate()
  const {optimisticStart, optimisticEnd} = useOptimisticCheckout()
  const showMessage = useMessage()

  async function submit({variables, ...rest}: Omit<UseMutateOptions<any, any>, 'mutation'>) {
    try {
      optimisticStart()
      setLoading(true)
      const result = await mutate({
        mutation,
        variables,
        clientName: options.clientName,
        ...rest
      })
      if (refetchQueries) {
        await client.refetchQueries({
          include: refetchQueries
        })
      }

      if (!options.noSuccessMessage) {
        showMessage(successMessage)
      }
      onSuccess(result)

      return result
    } catch (error) {
      showMessage(error)
      options.onError?.(error)
    } finally {
      setLoading(false)
      optimisticEnd()
    }
  }

  return {submit, loading}
}
